import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Logger } from '@utils/logger';
import { SmarshLiteratureListingService } from './services/smarsh-literature-listing.service';
import { takeUntil } from 'rxjs/operators';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import { Subject } from 'rxjs';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { LiteratureDocumentListingData, SegmentId } from '@types';
import { TranslateService } from '@shared/translate/translate.service';
import { fundNameGenerator, nameGenerator } from '@literature/literature-utils';
import { stringCompare } from '@utils/sort-utils';
import { SegmentService } from '@services';
import { NgxSpinnerService } from 'ngx-spinner';

const logger = Logger.getLogger('SmarshLiteratureListingComponent');

export interface TableData {
  name?: string;
  litCode?: string;
  publicationDate?: string;
  nextUpdateDate?: string;
  clientUse?: string;
}

@Component({
  selector: 'ft-smarsh-literature-listing',
  templateUrl: './smarsh-literature-listing.component.html',
  styleUrls: ['./smarsh-literature-listing.component.scss'],
})
export class SmarshLiteratureListingComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  @Input() component: BrComponent;

  private unsubscribe$: Subject<void> = new Subject<void>();
  currentSegment: SegmentId;
  paramMap: Map<string, string>;
  rowData: TableData[] = [];
  isInternational: boolean;
  isLoaded: boolean;

  constructor(
    private smarshLiteratureListingService: SmarshLiteratureListingService,
    protected literatureConfig: LiteratureGlobalConfigService,
    private translateService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private segmentService: SegmentService,
    private spinner: NgxSpinnerService
  ) {
    super();
    this.segmentService
      .getCurrentSegmentId$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((currentSegmentId: SegmentId) => {
        this.currentSegment = currentSegmentId;
      });
  }

  ngOnInit(): void {
    logger.debug('listing loaded');
    this.spinner
      .show('ListingDataLoadingSpinner')
      .then(() => logger.debug('Literature Data Spinner set'));
    this.paramMap = new Map();
    this.paramMap.set('cache', 'true');
    // TODO:- next Step will fetch it from BR
    this.literatureConfig.setLiteratureListingConfig({
      pageLayoutType: 'Documents Details View',
      pageType: 'investor-documents',
    });
    this.isInternational = this.literatureConfig.isInternational;
    this.smarshLiteratureListingService
      .register()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.rowData = [];
        this.populateLiteratureData(state.data);
      });

    this.smarshLiteratureListingService.populate(this.paramMap);
  }
  private populateLiteratureData(
    stateData: LiteratureDocumentListingData[]
  ): void {
    stateData.forEach((data) => {
      this.rowData.push(this.createRowData(data));
    });
    this.rowData = this.sortByName(this.rowData);
    this.isLoaded = true;
    this.changeDetector.detectChanges();
  }

  private createRowData(data: LiteratureDocumentListingData): TableData {
    return {
      name: this.isInternational
        ? nameGenerator(data, this.translateService)
        : fundNameGenerator(data, this.translateService),
      litCode: data?.literatureData?.docId || '-',
      clientUse: data?.literatureData?.clientUse || '-',
      publicationDate: data?.literatureData?.publicationDate || '-',
      nextUpdateDate: data?.literatureData?.nextUpdateDate || '-',
    };
  }

  private sortByName(data: TableData[]): TableData[] {
    return data.sort((a, b) => {
      return stringCompare(a?.name, b?.name, a, b, false);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
